import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions, useIsFocused } from '@react-navigation/native';
import { Stack, useNavigation, usePathname, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Dimensions, Modal, TouchableOpacity, View } from 'react-native';
import Autocomplete from 'react-native-autocomplete-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import AddressList from '@/components/AddressList';
import EngagementLimitDialog from '@/components/EngagementLimitDialog';
import Info from '@/components/Info';
import CrossPlatformMapView, { MapViewRef } from '@/components/MapView';
import { SyncStatusIndicator } from '@/components/SyncStatusIndicator';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import { Colors } from '@/constants/Colors';
import AppStyles from '@/constants/Styles';
import { useBackgroundAddresses, useBackgroundEngagementMapDetails, useBackgroundEngagements, useBackgroundMyActiveEngagements, useBackgroundPollingStationInfo, useBackgroundPostcodeSlicePolygons, useBackgroundStreetBreakdown } from '@/hooks/useBackgroundFetch';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useSafeKeepAwake } from '@/hooks/useSafeKeepAwake';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { Voter } from '@/types/canvassing';
import { authenticatedFetch } from '@/utils/auth';
import { calculateMapRegionFromFeatureCollection, extractPolygonsFromFeatureCollection } from '@/utils/geojson';

interface EngagementDetailsScreenProps {
  battlePlanId: string;
  engagementId: string;
  title?: string;
  placeholder?: string;
  wakeLockId?: string;
  isPhoneEngagement?: boolean;
}

export default function EngagementDetailsScreen({ 
  battlePlanId, 
  engagementId, 
  title = "Voters List",
  placeholder = "Search addresses, house names, numbers, or streets...",
  wakeLockId = "canvassing-engagement",
  isPhoneEngagement = false
}: EngagementDetailsScreenProps) {
  const { getSelectedBranch, getSelectedCampaign, getElectionForCampaign } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
   
  // Theme-aware colors using Colors.ts values
  const colorScheme = useColorScheme() ?? 'light';
  const backgroundColor = Colors[colorScheme].background;
  const borderColor = (Colors[colorScheme] as any).border;
  const cardBackground = (Colors[colorScheme] as any).cardBackground;
  const tintColor = Colors[colorScheme].tint;
  const iconColor = Colors[colorScheme].icon;
  const textColor = Colors[colorScheme].text;
  const inputBackground = (Colors[colorScheme] as any).inputBackground;
  const inputBorder = (Colors[colorScheme] as any).inputBorder;
  const formBackground = (Colors[colorScheme] as any).formBackground;
   
  const store = useCampaignStore.getState();
  const [actionError, setActionError] = useState<string | null>(null);
  
  // Use background fetch hook for all engagements
  const { data: allEngagements, loading: allEngagementsLoading, error: allEngagementsError, refetch: refetchAllEngagements } = useBackgroundEngagements(battlePlanId || '');
  
  // Use background fetch hook for my active engagements
  const { data: myActiveEngagements, error: myEngagementsError, refetch: refetchMyActiveEngagements } = useBackgroundMyActiveEngagements();
  
  // Use background fetch hook for engagement geometry
  const { data: geometryData, error: geometryError } = useBackgroundEngagementMapDetails(engagementId, battlePlanId);
  
  // Use background fetch hook for street breakdown
  const { data: streetBreakdown } = useBackgroundStreetBreakdown(engagementId);
  
  const [engagementInfo, setEngagementInfo] = useState<any>(null);
  const [isUserInEngagement, setIsUserInEngagement] = useState(false);
  
  // Conditional hook for postcode slice polygons - only call when user is in engagement
  const postcodePolygonsHook = useBackgroundPostcodeSlicePolygons(isUserInEngagement ? engagementId : '');
  
  // Conditional hook for polling station info - only call when user is in engagement
  const { data: pollingStationInfo } = useBackgroundPollingStationInfo(isUserInEngagement ? engagementId : '');
  
  // Get battle plan type for GOTV logic
  const battlePlanType = useMemo(() => {
    const getBattlePlans = useCampaignStore.getState().getBattlePlans;
    const battlePlans = getBattlePlans();
    const currentBattlePlan = battlePlans?.find(bp => bp.battle_plan_id === battlePlanId);
    return currentBattlePlan?.battle_plan_type;
  }, [battlePlanId]);
  
  // Create polling station markers from polling station info
  const pollingStationMarkers = useMemo(() => {
    if (!pollingStationInfo?.polling_station) return [];
    
    return [{
      id: `polling-station-${pollingStationInfo.polling_station.polling_station_id}`,
      coordinate: {
        latitude: pollingStationInfo.polling_station.latitude,
        longitude: pollingStationInfo.polling_station.longitude,
      },
      stationName: pollingStationInfo.polling_station.station_name,
      address: pollingStationInfo.polling_station.address,
      postcode: pollingStationInfo.polling_station.postcode,
    }];
  }, [pollingStationInfo]);
  
  // Provide fallback values when hook is not called
  const postcodeSlicePolygons = postcodePolygonsHook?.data || null;
  
  // Helper function to check if today is polling day
  const isPollingDay = useMemo(() => {
    if (!selectedCampaign || !selectedBranch) {
      return false;
    }
    
    try {
      const election = getElectionForCampaign(selectedCampaign, selectedBranch.branch_short_name);
      
      if (!election || !election.polling_day) {
        return false;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const pollingDate = new Date(election.polling_day);
      pollingDate.setHours(0, 0, 0, 0);
      
      return today.getTime() === pollingDate.getTime();
    } catch (error) {
      console.warn('Error checking polling day:', error);
      return false;
    }
  }, [selectedCampaign, selectedBranch, getElectionForCampaign]);
  
  // Conditional hook for addresses - only call when user is in engagement
  const addressesHook = useBackgroundAddresses(isUserInEngagement ? engagementId : '', undefined, isPollingDay);
  
  // Provide fallback values when hook is not called
  const addresses = addressesHook?.data || null;
  const loading = addressesHook?.loading || false;
  const fetchError = addressesHook?.error || null;
  const refetchAddresses = addressesHook?.refetch;
  
  const [leaving, setLeaving] = useState<string | null>(null); // Track which engagement is being left
  const [joining, setJoining] = useState(false);
  const [showEngagementLimitDialog, setShowEngagementLimitDialog] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // Track refresh state

  // Tab state
  const [activeTab, setActiveTab] = useState<'list' | 'map' | 'info'>('list');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Highlighted postcode state
  const [highlightedPostcode, setHighlightedPostcode] = useState<string | null>(null);
  const [hideSuggestions, setHideSuggestions] = useState(true);
  
  
  // Grouping state - default to false for phone engagements, true for others
  const [groupByStreet, setGroupByStreet] = useState<boolean>(!isPhoneEngagement);
  const [showGroupingModal, setShowGroupingModal] = useState(false);

  // Street expansion state - persists across tab switches
  const [expandedStreets, setExpandedStreets] = useState<Set<string>>(new Set());
  
  // Attempt group assignments - persists across tab switches, resets when leaving engagement
  // Format: address_id -> attemptNumber
  const attemptGroupAssignmentsRef = useRef<{ [addressId: string]: number }>({});
  const currentEngagementIdRef = useRef<string>(engagementId);
  
  // Clear assignments when engagement changes
  useEffect(() => {
    if (currentEngagementIdRef.current !== engagementId) {
      attemptGroupAssignmentsRef.current = {};
      currentEngagementIdRef.current = engagementId;
    }
  }, [engagementId]);
  
  // Initialize attempt group assignments when addresses are first loaded
  // This keeps the attempt groups static while viewing the engagement
  useEffect(() => {
    if (addresses && addresses.length > 0 && Object.keys(attemptGroupAssignmentsRef.current).length === 0) {
      const isGotvPlan = battlePlanType === 'gotv-door-knocking' || battlePlanType === 'gotv-phoning';
      const assignments: { [addressId: string]: number } = {};
      addresses.forEach((address: any) => {
        const attemptNumber = isGotvPlan 
          ? (address.total_interactions_this_engagement ?? 0)
          : (address.total_attempts_last_3_months ?? 0);
        assignments[address.address_id] = attemptNumber;
      });
      attemptGroupAssignmentsRef.current = assignments;
    }
  }, [addresses, battlePlanType]);
  
  // Function to get attempt number for an address (uses stored assignments if available)
  const getAttemptNumber = useCallback((address: any): number => {
    // First, try to get stored assignment (static value set when addresses were first loaded)
    const storedAttemptNumber = attemptGroupAssignmentsRef.current[address.address_id];
    if (storedAttemptNumber !== undefined) {
      return storedAttemptNumber;
    }
    
    // Fallback to calculating from address data
    const isGotvPlan = battlePlanType === 'gotv-door-knocking' || battlePlanType === 'gotv-phoning';
    if (isGotvPlan) {
      return address.total_interactions_this_engagement ?? 0;
    } else {
      return address.total_attempts_last_3_months ?? 0;
    }
  }, [battlePlanType]);
  
  // MapView ref for programmatic control
  const mapViewRef = useRef<MapViewRef>(null);
  
  const toggleStreetExpansion = (streetKey: string) => {
    setExpandedStreets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(streetKey)) {
        newSet.delete(streetKey);
      } else {
        newSet.add(streetKey);
      }
      return newSet;
    });
  };

  // Refresh function for pull-to-refresh
  const handleRefresh = async () => {
    if (!isUserInEngagement || !engagementId || !refetchAddresses) return;
    
    try {
      setRefreshing(true);
      // Use the refetch function from useBackgroundFetch which forces a fresh fetch
      await refetchAddresses();
    } catch (error) {
      console.warn('Failed to refresh addresses:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const router = useRouter();
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const screenHeight = Dimensions.get('window').height;
  const maxListHeight = Math.min(300, screenHeight * 0.4); // Max 250px or 40% of screen height, whichever is smaller

  // Derive error state from hook errors and action errors
  const error = useMemo(() => {
    if (actionError) return actionError;
    if (allEngagementsError) return allEngagementsError.message;
    if (myEngagementsError) return myEngagementsError.message;
    if (geometryError) return geometryError.message;
    if (fetchError) return fetchError.message;
    return null;
  }, [actionError, allEngagementsError, myEngagementsError, geometryError, fetchError]);

  const checkIfUserInEngagement = async () => {
    if (!allEngagements || !myActiveEngagements || !engagementId) return;

    const engagement = allEngagements.find((eng: any) => eng.engagement_id === engagementId);
    if (!engagement) {
      console.log('Engagement', engagementId, 'not found in all engagements');
      setIsUserInEngagement(false);
      return;
    }

    const isUserInEngagement = myActiveEngagements.some((eng: any) => eng.engagement_id === engagementId);
    if (isUserInEngagement) {
      await checkAndActivateEngagement(engagement);
      setActiveTab('list');
      setIsUserInEngagement(true);
    } else {
      setActiveTab('info');
      setIsUserInEngagement(false);
    }
  }

  useEffect(() => {
    checkIfUserInEngagement();
  }, [allEngagements, myActiveEngagements, engagementId]);

  // Separate useEffect to fetch engagement info when all engagements are loaded
  useEffect(() => {
    if (!allEngagementsLoading && isFocused) {
      fetchEngagementInfo();
    }
  }, [allEngagementsLoading, isFocused, engagementId, allEngagements]);

  // Clear postcode highlighting when switching away from map tab
  useEffect(() => {
    if (activeTab !== 'map') {
      setHighlightedPostcode(null);
    }
  }, [activeTab]);

  // Wake lock functionality - keep screen on during canvassing
  // Automatically disabled on iOS platforms by useSafeKeepAwake hook
  useSafeKeepAwake(wakeLockId, { 
    suppressDeactivateWarnings: true 
  });
  
  // Animation values for header and tabs (using only native animations)
  const headerTranslateY = useRef(new Animated.Value(0)).current;
  const tabsTranslateY = useRef(new Animated.Value(0)).current;
  const contentPaddingTop = useRef(new Animated.Value(insets.top + 56)).current;
  
  // Calculate bottom tab height (48px for tabs + 12px padding + safe area bottom)
  const bottomTabHeight = 48 + 12 + insets.bottom;
  const contentPaddingBottom = useRef(new Animated.Value(bottomTabHeight)).current;

  // Helper to check and activate engagement if needed
  const checkAndActivateEngagement = async (engagementData: any): Promise<void> => {
    try {
      // Check if engagement status is "open" and needs to be set to "active"
      if (engagementData.status === 'open') {
        const activateRes = await authenticatedFetch(
          selectedBranch?.branch_short_name,
          '/canvassing/start-engagement',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              engagement_id: engagementId
            }),
          },
          "groundforce"
        );
        
        if (!activateRes.ok) {
          console.warn(`Failed to activate engagement: ${activateRes.status}`);
        } else {
          // Engagement was successfully activated, refetch engagement data
          try {
            // Refetch both all engagements and my active engagements to get updated data
            await Promise.all([
              refetchAllEngagements(),
              refetchMyActiveEngagements()
            ]);
            
            // The data will be updated in the store and available through the hooks
            // We'll let the useEffect that watches allEngagements handle updating engagementInfo
          } catch (refetchError) {
            console.warn('Error refetching engagement data after activation:', refetchError);
            // Don't throw error here as the activation was successful
          }
        }
      }
    } catch (err) {
      console.warn('Error activating engagement:', err);
      // Don't throw error here as this is not critical for loading voters
    }
  };

  // Helper to fetch engagement info
  const fetchEngagementInfo = async () => {
    try {      
      if (!engagementId) return;
      
      // Check for errors
      if (allEngagementsError) {
        console.error('All engagements error in fetchEngagementInfo:', allEngagementsError);
        setActionError(`Failed to load engagements: ${allEngagementsError.message}`);
        return;
      }
      
      // First, find the engagement in all engagements
      const engagement = allEngagements?.find((eng: any) => eng.engagement_id === engagementId);
      if (!engagement) {
        console.log('Engagement', engagementId, 'not found in all engagements');
        setActionError('Engagement not found');
        return;
      }
      
      setEngagementInfo(engagement);
      
    } catch (err) {
      console.error('Error fetching engagement info:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to load engagement info');
    }
  };

  // Map helper functions
  const initialRegion = useMemo(() => {
    return calculateMapRegionFromFeatureCollection(geometryData?.geometry, 0.1);
  }, [geometryData]);

  // Convert geometry to MapPolygon[]
  const polygons = useMemo(() => {
    if (!geometryData || !geometryData.geometry || !geometryData.geometry.features) return [];
    
    // If user has not joined the engagement, show standard fill color
    // If user has joined, show transparent fill (current behavior)
    const fillColor = isUserInEngagement ? 'rgba(0,122,255,0)' : 'rgba(0,122,255,0.2)';
    
    return extractPolygonsFromFeatureCollection(
      { type: 'FeatureCollection', features: geometryData.geometry.features },
      {
        idPrefix: 'polygon',
        strokeColor: '#007AFF',
        fillColor: fillColor,
        strokeWidth: 2,
        propertyExtractor: (feature, index) => feature.id || `engagement-${engagementId}-${index}`,
      }
    );
  }, [geometryData, engagementId, isUserInEngagement]);

  // Check if current query is a house number (can include letters like 10a, 15b)
  const isNumberQuery = useMemo(() => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery === '') return false;
    
    // Check if it starts with a number and optionally ends with letters
    // This matches patterns like: 123, 10a, 15b, 1, etc.
    const houseNumberPattern = /^\d+[a-zA-Z]*$/;
    return houseNumberPattern.test(trimmedQuery);
  }, [searchQuery]);

  // Generate autocomplete suggestions (only for text queries)
  const autocompleteSuggestions = useMemo(() => {
    // Don't show suggestions for number queries
    if (isNumberQuery) {
      return [];
    }

    const suggestions = new Set<string>();
    
    (addresses || []).forEach((address: any) => {
      // Add house names (only for text queries)
      if (address.house_name) {
        suggestions.add(address.house_name);
      }
      
      // Add street names
      if (address.street_name) {
        suggestions.add(address.street_name);
      }
      
      // Add street sub names
      if (address.street_sub_name) {
        suggestions.add(address.street_sub_name);
      }
      
      // Add postcodes
      if (address.postcode) {
        suggestions.add(address.postcode);
      }
    });
    
    // Filter suggestions based on current search query
    const filteredSuggestions = Array.from(suggestions).filter(suggestion =>
      suggestion.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    return filteredSuggestions.slice(0, 10); // Limit to 10 suggestions
  }, [addresses, searchQuery, isNumberQuery]);

  // Filter addresses by visited/not_in status only (for postcode polygons)
  const addressesFilteredByStatus = useMemo(() => {
    let filtered = addresses || [];

    // Check if this is a GOTV door-knocking battle plan
    const isGotvDoorKnocking = battlePlanType === 'gotv-door-knocking';

    // Apply GOTV-specific logic for door-knocking battle plans
    if (isGotvDoorKnocking) {
      filtered = filtered.map((address: any) => {
        // Get primary target group voters (in_primary_target_group = true)
        const primaryTargetVoters = address.voters?.filter((voter: Voter) => voter.in_primary_target_group === true) || [];
        
        // If no primary target voters, return address unchanged
        if (primaryTargetVoters.length === 0) {
          return address;
        }

        // Check if all primary target voters meet the criteria for marking as visited
        const allPrimaryTargetsCompleted = primaryTargetVoters.every((voter: Voter) => {
          // Voter has voted
          if (voter.voted_yet === true) {
            return true;
          }
          
          // Voter will not vote
          if (voter.will_not_vote === true) {
            return true;
          }

          if (voter.requested_lift === true) {
            return true;
          }

          if (voter.in_primary_target_group != true) {
            return true;
          }
          
          // Voter said they will vote, but the time of voting has not yet passed
          if (voter.will_vote_by_time) {
            try {
              const voteByTime = new Date(voter.will_vote_by_time);
              const now = new Date();
              
              // If the vote by time is in the future, mark as completed
              if (voteByTime > now) {
                return true;
              }
            } catch (error) {
              console.warn('Failed to parse will_vote_by_time for voter:', voter.voter_id, error);
              // If parsing fails, don't mark as completed
              return false;
            }
          }
          
          // Voter doesn't meet completion criteria
          return false;
        });

        // Mark address as visited if all primary target voters are completed
        if (allPrimaryTargetsCompleted) {
          return {
            ...address,
            visited: true
          };
        }

        return address;
      });
    }

    return filtered;
  }, [addresses, battlePlanId]);

  // Filter addresses based on search query and exclude filters (for address list)
  const filteredAddresses = useMemo(() => {
    const filtered = addressesFilteredByStatus;

    // Apply search query filter
    if (!searchQuery.trim()) {
      return filtered;
    }

    const query = searchQuery.toLowerCase().trim();
    
    return filtered.filter((address: any) => {
      // If query is a number, only match exact house numbers
      if (isNumberQuery) {
        return address.house_number?.toLowerCase() === query;
      }
      
      // For non-number queries, search across all components
      // Search in house name
      if (address.house_name?.toLowerCase().includes(query)) return true;
      
      // Search in street name
      if (address.street_name?.toLowerCase().includes(query)) return true;
      
      // Search in street sub name
      if (address.street_sub_name?.toLowerCase().includes(query)) return true;
      
      // Search in postcode
      if (address.postcode?.toLowerCase().includes(query)) return true;
      
      return false;
    });
  }, [addressesFilteredByStatus, searchQuery, isNumberQuery]);

  // Convert postcode slice polygons to MapPolygon[] - only show postcodes with addresses to visit
  const postcodePolygons = useMemo(() => {
    if (!postcodeSlicePolygons || !postcodeSlicePolygons.features) {
      return [];
    }
    
    // Get addresses filtered by status only (visited/not_in filters, no search query)
    const activeAddresses = addressesFilteredByStatus || [];
    
    // Filter to only include unvisited addresses that are not marked as "not in"
    // (addresses that still need to be visited)
    const unvisitedAddresses = activeAddresses.filter((address: any) => 
      !address.visited && !address.not_in
    );
    
    // Extract unique postcodes from unvisited addresses only
    const activePostcodes = new Set(
      unvisitedAddresses
        .map((address: any) => address.postcode)
        .filter((postcode: string) => postcode && postcode.trim() !== '')
    );
    
    // Filter postcode features to only include those with unvisited addresses (excluding visited and "not in" addresses)
    // Always include the highlighted postcode even if it's filtered out (e.g., all addresses visited)
    const filteredFeatures = postcodeSlicePolygons.features.filter((feature: any) => {
      const postcode = feature.properties?.postcode;
      if (!postcode) return false;
      // Always include highlighted postcode, even if all addresses are visited
      if (highlightedPostcode && postcode === highlightedPostcode) {
        return true;
      }
      return activePostcodes.has(postcode);
    });
    
    // Create filtered feature collection
    const filteredFeatureCollection = {
      ...postcodeSlicePolygons,
      features: filteredFeatures
    };
    
    return extractPolygonsFromFeatureCollection(filteredFeatureCollection, {
      idPrefix: 'postcode',
      strokeColor: '#28a745', // Default green for normal postcodes
      fillColor: 'rgba(0,122,255,0.2)', // Default blue fill
      strokeWidth: 2, // Default stroke width
      propertyExtractor: (feature) => feature.properties?.postcode || 'unknown',
      styleExtractor: (feature) => {
        const postcode = feature.properties?.postcode || 'unknown';
        const isHighlighted = highlightedPostcode && postcode === highlightedPostcode;
        
        return {
          strokeColor: isHighlighted ? '#FF6B35' : '#28a745', // Orange for highlighted, green for normal
          fillColor: isHighlighted ? 'rgba(255, 107, 53, 0.3)' : 'rgba(0,122,255,0.2)', // Orange fill for highlighted
          strokeWidth: isHighlighted ? 3 : 2, // Thicker stroke for highlighted
        };
      },
      onPress: (feature) => {
        const postcode = feature.properties?.postcode || 'unknown';
        // Clicking on postcode area should search for that postcode
        setSearchQuery(postcode);
        setActiveTab('list');
      },
    });
  }, [postcodeSlicePolygons, addressesFilteredByStatus, addresses, filteredAddresses, highlightedPostcode]);

  // Combine engagement and postcode polygons
  const allPolygons = useMemo(() => {
    return [...polygons, ...postcodePolygons];
  }, [polygons, postcodePolygons]);

  // Scroll handler for hiding/showing header and tabs
  const lastScrollY = useRef(0);
  const handleScroll = (event: any) => {
    const currentOffset = event.nativeEvent.contentOffset.y;
    const previousOffset = lastScrollY.current;
    const diff = currentOffset - previousOffset;
    lastScrollY.current = currentOffset;
    
    if (diff > 10) {
      // Scrolling down - hide header, tabs, and dropdown
      setHideSuggestions(true); // Hide dropdown when scrolling down
      Animated.parallel([
        Animated.timing(headerTranslateY, {
          toValue: -100,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(tabsTranslateY, {
          toValue: 100,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(contentPaddingTop, {
          toValue: insets.top,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(contentPaddingBottom, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start();
    } else if (diff < -10) {
      // Scrolling up - show header and tabs
      Animated.parallel([
        Animated.timing(headerTranslateY, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(tabsTranslateY, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(contentPaddingTop, {
          toValue: insets.top + 56,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(contentPaddingBottom, {
          toValue: bottomTabHeight,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  // Helper function to check engagement limit for current branch
  const checkEngagementLimit = (): boolean => {
    const userEngagementsInBranch = myActiveEngagements || [];
    
    // Check if user is already at the limit (4 or more engagements)
    if (userEngagementsInBranch.length >= 4) {
      setShowEngagementLimitDialog(true);
      return true; // Limit exceeded
    }
    
    return false; // Under limit, can proceed
  };

  // Helper functions for engagement actions
  const handleJoinEngagement = async () => {
    // Check engagement limit before attempting to join
    if (checkEngagementLimit()) {
      return; // Dialog already shown, don't proceed
    }
    
    try {
      setJoining(true);
      const joinRes = await authenticatedFetch(
        selectedBranch?.branch_short_name,
        '/canvassing/join-engagement',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            engagement_id: engagementId
          }),
        },
        "groundforce"
      );
      
      if (!joinRes.ok) throw new Error(`Failed to join engagement: ${joinRes.status}`);
      
      // Engagement was successfully joined, refetch engagement data
      try {
        // Refetch both all engagements and my active engagements to get updated data
        await Promise.all([
          refetchMyActiveEngagements()
        ]);
        
        // The data will be updated in the store and available through the hooks
        // We'll let the useEffect that watches allEngagements handle updating engagementInfo
      } catch (refetchError) {
        console.warn('Error refetching engagement data after join:', refetchError);
        // Don't throw error here as the join was successful
      }
      
    } catch (err) {
      console.error('Error joining engagement:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to join engagement');
    } finally {
      setJoining(false);
    }
  };

  // Common function for leaving an engagement
  const leaveEngagement = async (engagementIdToLeave: string, onSuccess?: () => void) => {
    try {
      setLeaving(engagementIdToLeave);
      const leaveRes = await authenticatedFetch(
        selectedBranch?.branch_short_name,
        '/canvassing/leave-engagement',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            engagement_id: engagementIdToLeave
          }),
        },
        "groundforce"
      );
      
      if (!leaveRes.ok) throw new Error(`Failed to leave engagement: ${leaveRes.status}`);

      // Clear all data for this engagement from the store
      store.clearEngagementData(engagementIdToLeave);
      
      // Refresh active engagements
      store.fetchMyActiveEngagements()
      
      // Execute custom success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error leaving engagement:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to leave engagement');
    } finally {
      setLeaving(null);
    }
  };

  const handleLeaveEngagement = async () => {
    await leaveEngagement(engagementId, () => {
      // Navigate back to battle plan screen
      router.back();
    });
  };

  const handleLeaveEngagementFromDialog = async (engagementIdToLeave: string) => {
    await leaveEngagement(engagementIdToLeave, async () => {
      // Close the dialog
      setShowEngagementLimitDialog(false);
      
      // Reset the leaving state to ensure clean state
      setLeaving(null);
    });
  };

  if (loading) {
    return <ThemedView style={{ flex: 1 }}><ActivityIndicator size="large" /></ThemedView>;
  }

  if (error) {
    return <ThemedView style={{ flex: 1 }}><ThemedText style={AppStyles.errorText}>{error}</ThemedText></ThemedView>;
  }

  return (
    <ThemedView style={{ flex: 1 }}>
      <Stack.Screen
        options={{
          headerShown: false, // Hide the default header
        }}
      />
      
      {/* Sync Status Indicator */}
      <SyncStatusIndicator showDetails={true} />
      
      {/* Custom Header */}
      <Animated.View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: backgroundColor,
        borderBottomWidth: 1,
        borderBottomColor: borderColor,
        paddingTop: insets.top,
        transform: [{ translateY: headerTranslateY }],
      }}>
        {/* Header Content */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 15,
          paddingVertical: 12,
          minHeight: 56,
        }}>
          {/* Left Button */}
          <TouchableOpacity 
            onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} 
            style={{ 
              padding: 8,
              marginRight: 12,
            }}
          >
            <IconSymbol name="chevron.right" size={24} color={tintColor} />
          </TouchableOpacity>

          {/* Search Box */}
          {isUserInEngagement ? (
            <View style={{ flex: 1, position: 'relative' }}>
              <Autocomplete
                data={autocompleteSuggestions}
                value={searchQuery}
                hideResults={hideSuggestions}
                onChangeText={text => {
                  setSearchQuery(text);
                  setHideSuggestions(false);
                  // Switch to list tab when typing
                  setActiveTab('list');
                }}
                onFocus={() => {
                  setHideSuggestions(false); // Show dropdown immediately on focus
                  // Switch to list tab when focusing
                  setActiveTab('list');
                }}
                placeholder={placeholder}
                placeholderTextColor={iconColor}
                containerStyle={{ flex: 1 }}
                inputContainerStyle={{
                  borderWidth: 1,
                  borderColor: inputBorder,
                  borderRadius: 8,
                  paddingHorizontal: 10,
                  backgroundColor: inputBackground,
                }}
                style={{
                  fontSize: 16,
                  color: textColor,
                  backgroundColor: inputBackground,
                }}
                listContainerStyle={{
                  borderWidth: 1,
                  borderColor: inputBorder,
                  borderRadius: 8,
                  backgroundColor: formBackground,
                  maxHeight: maxListHeight,
                  marginTop: 5,
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1001,
                }}
                flatListProps={{
                  keyboardShouldPersistTaps: 'always',
                  keyExtractor: (item: any, index: number) => `${item}-${index}`,
                  style: { borderWidth: 0 }, // Remove inner border from FlatList
                  renderItem: ({ item }: { item: any }) => {
                    return (
                      <TouchableOpacity
                        style={{
                          paddingHorizontal: 12,
                          paddingVertical: 10,
                          borderBottomWidth: 1,
                          borderBottomColor: borderColor,
                          backgroundColor: formBackground,
                        }}
                        onPress={() => {
                          setSearchQuery(item);
                          setHideSuggestions(true);
                        }}
                      >
                        <ThemedText style={{ fontSize: 16 }}>{item}</ThemedText>
                      </TouchableOpacity>
                    );
                  },
                }}
              />
              {(!hideSuggestions || searchQuery.trim() !== '') && (
                <TouchableOpacity
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: [{ translateY: -12 }],
                    zIndex: 1000,
                    padding: 5,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={() => {
                    setSearchQuery('');
                    setHideSuggestions(true);
                  }}
                >
                  <MaterialIcons name="close" size={20} color={iconColor} />
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <View style={{ flex: 1 }}>
              <ThemedText style={[AppStyles.headerText, { fontSize: 18 }]}>{title}</ThemedText>
            </View>
          )}

          {/* Right Buttons */}
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {/* Grouping Icon - only show on list tab when user is in engagement */}
            {activeTab === 'list' && isUserInEngagement && (
              <TouchableOpacity 
                onPress={() => setShowGroupingModal(true)}
                style={{ 
                  padding: 8,
                  marginLeft: 12,
                }}
              >
                <MaterialIcons name="view-list" size={24} color={tintColor} />
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              onPress={() => router.push({ 
                pathname: '/(main)/[branch]/share', 
                params: { 
                  branch: selectedBranch?.branch_short_name || "", 
                  ref: pathname,
                  battlePlanId: battlePlanId,
                  engagementId: engagementId
                } 
              })}
              style={{ 
                padding: 8,
                marginLeft: 12,
              }}
            >
              <IconSymbol name="square.and.arrow.up" size={24} color="#007AFF" />
            </TouchableOpacity>
          </View>
        </View>
      </Animated.View>
      
      {/* Main Content Area with dynamic padding for header and tabs */}
      <Animated.View style={{ 
        flex: 1, 
        paddingTop: contentPaddingTop,
        paddingBottom: contentPaddingBottom,
      }}>
        {/* Content based on active tab */}
        {activeTab === 'list' && isUserInEngagement ? (
          <AddressList 
            addresses={filteredAddresses}
            engagementId={engagementId}
            battlePlanId={battlePlanId}
            searchQuery={searchQuery}
            onScroll={handleScroll}
            onShowAddressOnMap={(postcode: string) => {
              setHighlightedPostcode(postcode);
              setActiveTab('map');
              
              // Zoom to the postcode after a short delay to ensure map is rendered
              setTimeout(() => {
                if (mapViewRef.current?.zoomToPostcode) {
                  mapViewRef.current.zoomToPostcode(postcode);
                }
              }, 100);
            }}
            expandedStreets={expandedStreets}
            onToggleStreet={toggleStreetExpansion}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            isPhoneEngagement={isPhoneEngagement}
            battlePlanType={battlePlanType}
            getAttemptNumber={getAttemptNumber}
            groupByStreet={groupByStreet}
            onGroupByStreetChange={setGroupByStreet}
          />
        ) : activeTab === 'map' ? (
          /* Map View */
          <View style={{ flex: 1 }}>
            {geometryData && allPolygons.length > 0 ? (
              <CrossPlatformMapView
                ref={mapViewRef}
                initialRegion={initialRegion}
                polygons={allPolygons}
                pollingStationMarkers={pollingStationMarkers}
                showUserLocation={true}
                postcodeSlicePolygons={postcodeSlicePolygons}
                style={{ flex: 1, width: '100%' }}
              />
            ) : (
              <View style={AppStyles.emptyContainer}>
                <ThemedText>Map data not available</ThemedText>
              </View>
            )}
          </View>
        ) : activeTab === 'info' ? (
          <Info 
            engagementInfo={engagementInfo}
            isUserInEngagement={isUserInEngagement}
            onJoinEngagement={handleJoinEngagement}
            onLeaveEngagement={handleLeaveEngagement}
            joining={joining}
            leaving={leaving === engagementId}
            insets={insets}
            engagementId={engagementId}
            streetBreakdown={streetBreakdown}
            pollingStationInfo={pollingStationInfo}
            battlePlanType={battlePlanType}
          />
        ) : null}
      </Animated.View>

      {/* Bottom Tab Navigation */}
      <Animated.View style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        backgroundColor: backgroundColor,
        borderTopWidth: 1,
        borderTopColor: borderColor,
        paddingBottom: insets.bottom,
        transform: [{ translateY: tabsTranslateY }],
      }}>
        {isUserInEngagement && (
          <TouchableOpacity
            style={[
              {
                flex: 1,
                paddingVertical: 12,
                alignItems: 'center',
                justifyContent: 'center',
              },
              activeTab === 'list' && { backgroundColor: cardBackground }
            ]}
            onPress={() => setActiveTab('list')}
          >
            <MaterialIcons 
              name="list" 
              size={24} 
              color={activeTab === 'list' ? tintColor : iconColor} 
            />
            <ThemedText style={[
              { fontSize: 12, marginTop: 4 },
              activeTab === 'list' ? { color: tintColor } : { color: iconColor }
            ]}>List</ThemedText>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            activeTab === 'map' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setActiveTab('map')}
        >
          <MaterialIcons 
            name="map" 
            size={24} 
            color={activeTab === 'map' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            activeTab === 'map' ? { color: tintColor } : { color: iconColor }
          ]}>Map</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            activeTab === 'info' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setActiveTab('info')}
        >
          <MaterialIcons 
            name="info" 
            size={24} 
            color={activeTab === 'info' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            activeTab === 'info' ? { color: tintColor } : { color: iconColor }
          ]}>Info</ThemedText>
        </TouchableOpacity>
      </Animated.View>

      {/* Grouping Modal */}
      <Modal
        visible={showGroupingModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowGroupingModal(false)}
      >
        <TouchableOpacity 
          style={{ 
            flex: 1, 
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center'
          }} 
          activeOpacity={1} 
          onPress={() => setShowGroupingModal(false)}
        >
          <View style={{ 
            backgroundColor: cardBackground,
            borderRadius: 12,
            padding: 20,
            minWidth: 280,
            maxWidth: '80%',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 5,
          }}>
            <View style={{ 
              flexDirection: 'row', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16
            }}>
              <ThemedText style={[AppStyles.headerText, { fontSize: 18 }]}>Grouping</ThemedText>
              <TouchableOpacity
                onPress={() => setShowGroupingModal(false)}
                style={{ padding: 4 }}
              >
                <MaterialIcons name="close" size={24} color={iconColor} />
              </TouchableOpacity>
            </View>
            
            <TouchableOpacity
              style={{
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: groupByStreet ? tintColor + '20' : 'transparent',
                marginBottom: 8,
              }}
              onPress={() => {
                setGroupByStreet(!groupByStreet);
                setShowGroupingModal(false);
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {groupByStreet && (
                  <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                )}
                <ThemedText style={{ 
                  fontSize: 16,
                  color: groupByStreet ? tintColor : undefined,
                  marginLeft: groupByStreet ? 0 : 28
                }}>
                  By Street
                </ThemedText>
              </View>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Engagement Limit Dialog */}
      <EngagementLimitDialog
        visible={showEngagementLimitDialog}
        onClose={() => setShowEngagementLimitDialog(false)}
        myActiveEngagements={myActiveEngagements || []}
        onLeaveEngagement={handleLeaveEngagementFromDialog}
        leaving={leaving}
      />
    </ThemedView>
  );
}
